import React from "react";
import { useState, useEffect } from "react"
import { useSelector } from "react-redux"
import { RootState } from "../../../store/store"
import { CustomHeader } from "../../../components/headers/CustomHeader"
import { Button, Card, Checkbox, Tooltip } from "@material-tailwind/react"
import {
   Input,
   Spinner,
   Typography,
} from "@material-tailwind/react"
import { useQuery } from "@apollo/client"
import { BASEURL, DEFAULTPRIMARYCOLOR, id_types } from "../../../util"
import { SubmitHandler, useFieldArray, useForm } from "react-hook-form"
import { ToastContainer, toast } from "react-toastify"
import { getColorCode } from "../../../colorCodes"
import { BiPlus , BiTrash} from "react-icons/bi"
import { FiX } from "react-icons/fi"
import { FaSave } from "react-icons/fa";
import { GETCOMPANYSTAFF, GET_DRIVER_STATIONS } from "../../../GraphQl/queries"


type Driver = {
   first_name: string;
   last_name: string;
   phone_number: string;
   balance: string;
   station: string;
   dob: string;
   address?: string;
   created_by: string;
}

type FormDataType = {
   drivers: Driver[];
}


type FormType = {
   first_name: string
   last_name: string
   dob: string
   balance: string
   phone_number: string
   address: string
   station: string
   date_created: string
   created_by: string
}

export type DriverResponseType = {
   driver_id: string
   first_name: string
   last_name: string
   dob: string
   balance: string
   phone_number: string
   address: string
   station: string
   created_by: string
   date_created: string
}

export type DriversDataType = {
   account_status: string
   balance: number
   date_created: string
   driver_code: string
   driver_id: string
   driver_name: string
   phone_number: string
   total_number_of_policies: number
   total_premium_balance: number
   total_premium_paid: number
   total_premium_payable: number
   total_arrears: number
   wallet_number: string
   usertype: string
   created_by: string
}

type StaffResponseType = {
   id: string
   name: string
}

type DriverStationType = {
   id: string
   name: string
}
 
type StationsType = {
   driver_stations: DriverStationType[] 
}

type DriverFormEntry = Partial<FormType>;

const AddDriver = () => {
   const {
      companyId,
      user: stateUser,
      token,
   } = useSelector((state: RootState) => state.auth)

   const [temporaryDrivers, setTemporaryDrivers] = useState<Driver[]>([]);
   const [loadingSubmission, setLoadingSubmission] = useState(false);

   const {
      register,
      handleSubmit,
      reset,
      formState: { isSubmitting, errors: errors },
      setValue,
      control,
      watch,
   } = useForm<FormDataType>()


   const {
      data: staff,
      loading: loadingStaff,
      refetch: refreshStaff,
   } = useQuery<{
      company_staff: StaffResponseType[]
   }>(GETCOMPANYSTAFF, {
      variables: {
         company_id: companyId,
      },
   })

   
   const { 
      data: stations, 
      loading, 
      refetch,
   } = useQuery<{
      driver_stations:DriverStationType
   }>(GET_DRIVER_STATIONS)

   
   const addDriver = (data: any) => {
      const driver = data.drivers?.[0]; // get first driver

      if (!driver?.first_name && !driver?.phone_number) {
         toast.error("Please fill at least the first name or phone number");
         return;
      }

      setTemporaryDrivers((prev) => [...prev, driver]);
      // console.log("Added:", driver);
      reset();                         
   };

   const removeTempDriver = (index: any) => {
      const updated = [...temporaryDrivers];
      updated.splice(index, 1);
      setTemporaryDrivers(updated);
   };


   const submitAllDrivers = async () => {
      try {
         setLoadingSubmission(true);
         const payload = temporaryDrivers.map((driver) => ({
            first_name: driver.first_name,
            last_name: driver.last_name,
            dob: driver.dob,
            balance: driver.balance,
            address: driver.address,
            station: driver.station,
            phone_number: driver.phone_number,
            created_by: driver.created_by,
         }));

         if (payload.length === 0) {
            toast.error("Please fill at least one driver");
            return;
         }

         let options = {
            method: "POST",
            headers: {
               "Content-Type": "application/json",
               Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(payload),
         }

         

         const req = await fetch(`${process.env.REACT_APP_BASE_URL}/add-drivers-bulk`, options)
         const res = await req.json()
         setLoadingSubmission(false);

         if (req.ok) {
            if (res?.data?.id || (req?.status >= 200 && req?.status < 300)) {
            toast.success(res?.message ?? "Driver(s) created successfully")
            reset()
            reset({ drivers: [{}] });
            setTemporaryDrivers([])
            } else {
            toast.error(res?.detail ?? res?.data ?? "A error occurred")
            }
         } else {
            toast.error(res?.message ?? "An error occured")
         }
      } catch (error: any) {

         toast.error(error?.message ?? "Something went wrong")
      }
   }

   const userData = stateUser
   const [colors, setColors] = useState<string[]>()

   useEffect(() => {
      if (userData) {
         const colors = getColorCode(stateUser?.colors);
         setColors((prev) => {
            const same = JSON.stringify(prev) === JSON.stringify(colors);
            return same ? prev : colors;
         });
      }
   }, [userData, stateUser?.colors]);


   const onError = (errors: any) => {
      if (errors.phone_number) {
         toast.error(errors.phone_number.message);
      }
      // Add toasts for other fields if needed
   };


   return (
      <>
         <CustomHeader title="Add Drivers" />
         <div className="flex flex-col md:flex-row gap-10">
            <form
               onSubmit={handleSubmit(addDriver)}
               className="w-full gap-10 flex flex-col md:flex-row"
            >
               <div className="w-full md:w-1/2 flex flex-col">
                  <div
                     style={{
                        background:
                           colors?.length! > 1
                              ? `${colors![0]?.toLowerCase()}`
                              : `${DEFAULTPRIMARYCOLOR}`,
                     }}
                     className="p-3"
                  >
                     <div className="flex w-full">
                        <Typography className="text-base text-white">
                           Driver Details
                        </Typography>
                     </div>
                  </div>
                  <div className="p-3 bg-white rounded-b-xl w-full">
                     <Typography className="text-base text-white">
                        Driver Details
                     </Typography>
                     <div className="flex flex-col gap-4">
                        {/* First Name */}
                        <div className="flex flex-col lg:flex-row gap-2">
                           <Typography className="w-1/3 font-semibold text-sm">
                              First Name <span className="text-red-600">*</span>
                           </Typography>
                           <div className="w-full">
                              <Input
                                 crossOrigin="anonymous"
                                 size="md"
                                 {...register("drivers.0.first_name", { required: "First Name is required" })}
                              />
                              {errors?.drivers?.[0]?.first_name?.message && (
                                 <p className="text-red-600 text-xs mt-1">
                                    {errors.drivers[0].first_name.message}
                                 </p>
                              )}
                           </div>
                        </div>

                        <div className="flex flex-col lg:flex-row gap-2">
                           <Typography className="w-1/3 font-semibold text-sm">
                              Last Name <span className="text-red-600">*</span>
                           </Typography>
                           <div className="w-full">
                              <Input
                                 crossOrigin="anonymous"
                                 size="md"
                                 {...register("drivers.0.last_name", { required: "Last Name is required" })}
                              />
                              {errors?.drivers?.[0]?.last_name?.message && (
                                 <p className="text-red-600 text-xs mt-1">
                                    {errors.drivers[0].last_name.message}
                                 </p>
                              )}
                           </div>
                        </div>

                        {/* Station */}
                        <div className="w-full flex flex-col md:flex-col lg:flex-row my-1 gap-1 items-center">
                           <Typography
                              variant="h6"
                              color="blue-gray"
                              className="mb-2 font-semibold text-[14px] w-1/3"
                           >
                              Station <span className=" text-red-600">*</span>
                           </Typography>
                           <select
                           {...register("drivers.0.station", { required: "Station is required" })}
                           className="h-11 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                           >
                           <option value="">Select Station</option>
                           {Array.isArray(stations?.driver_stations) &&
                           (stations?.driver_stations ?? [])
                           .slice()
                           .sort((a, b) => a.name.localeCompare(b.name))
                           .map((station:any) => (
                              <option key={station.id} value={station.id}>
                                 {station.name}
                              </option>
                           ))}
                           </select>
                        </div>

                        <div className="flex flex-col lg:flex-row gap-2">
                           <Typography className="w-1/3 font-semibold text-sm">
                              Balance <span className="text-red-600">*</span>
                           </Typography>
                           <div className="w-full">
                              <Input
                                 crossOrigin="anonymous"
                                 size="md"
                                 {...register("drivers.0.balance", { required: "Balance is required" })}
                              />
                              {errors?.drivers?.[0]?.balance?.message && (
                                 <p className="text-red-600 text-xs mt-1">
                                    {errors.drivers[0].balance.message}
                                 </p>
                              )}
                           </div>
                        </div>

                        {/* Date of Birth */}
                        <div className="w-full flex flex-col md:flex-col lg:flex-row my-1 gap-1 items-center">
                           <Typography
                              variant="h6"
                              color="blue-gray"
                              className="mb-2 font-semibold text-[14px] w-1/3"
                           >
                              Date of Birth <span className=" text-red-600">*</span>
                           </Typography>
                           <Input
                              crossOrigin="anonymous"
                              type="date"
                              size="md"
                              className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                              labelProps={{
                                 className: "before:content-none after:content-none",
                              }}
                              {...register("drivers.0.dob", { required: "Date of birth is required" })}
                           />
                        </div>

                        {/* Address */}
                        <div className="w-full flex flex-col md:flex-col lg:flex-row my-1 gap-1 items-center">
                           <Typography
                              variant="h6"
                              color="blue-gray"
                              className="mb-2 font-semibold text-[14px] w-1/3"
                           >
                              Address
                           </Typography>
                           <Input
                              crossOrigin="anonymous"
                              size="md"
                              className=" !border-t-blue-gray-200 focus:!border-t-gray-900"
                              labelProps={{
                                 className: "before:content-none after:content-none",
                              }}
                              {...register("drivers.0.address")}
                           />
                        </div>

                        {/* Created By*/}       
                        <div className="w-full flex flex-row my-1 gap-1 items-center">
                           <Typography
                           variant="h6"
                           color="blue-gray"
                           className="mb-2 font-semibold text-[14px] w-1/3"
                           >
                           Created By
                           </Typography>
                           <select
                              {...register("drivers.0.created_by", { required: "Creator is required" })}
                              className="h-11 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                           >
                           <option value="">Select Staff</option>
                           {(staff?.company_staff ?? [])
                           .slice()
                           .sort((a, b) => a.name.localeCompare(b.name))
                           .map((user) => (
                              <option key={user.id} value={user.id}>
                                 {user.name}
                              </option>
                           ))}
                           </select>
                        </div>

                        <div className="flex flex-col lg:flex-row gap-2">
                           <Typography className="w-1/3 font-semibold text-sm">
                              Phone number <span className="text-red-600">*</span>
                           </Typography>
                           <div className="w-full">
                              <Input
                                 crossOrigin="anonymous"
                                 size="md"
                                 {...register("drivers.0.phone_number", {
                                    required: "Phone number is required",
                                    onChange: (e) => {
                                       const cleaned = e.target.value.replace(/\s+/g, "");
                                       setValue("drivers.0.phone_number", cleaned);
                                    },
                                    validate: (value) => {
                                       if (!/^\d+$/.test(value)) {
                                          return "Phone number must contain only digits";
                                       }
                                       if (value.length < 10 || value.length > 10) {
                                          return "Phone number must be exactly 10 digits";
                                       }
                                       return true;
                                    }
                                 })}
                              />
                              {errors?.drivers?.[0]?.phone_number?.message && (
                                 <p className="text-red-600 text-xs mt-1">
                                    {errors.drivers[0].phone_number.message}
                                 </p>
                              )}
                           </div>
                        </div>


                     </div>
                  </div>
                  <Button type="submit" className="self-end mt-3 bg-blue-600 text-white flex flex-row items-center">
                     <BiPlus size={15} className="mr-1" />
                     Add Driver
                  </Button>
               </div>

               <div className="w-full md:w-1/2">
                  <div className="flex items-center justify-between mb-3">
                     <Typography variant="h6" className='text-white'>Added Drivers</Typography>
                  </div>

                  <div className="overflow-x-auto border rounded">
                     <table className="min-w-full divide-y divide-gray-200 text-sm">
                        <thead className="bg-gray-100 text-center">
                           <tr>
                              <th className="p-2">First Name</th>
                              <th className="p-2">Last Name</th>
                              <th className="p-2">Phone</th>
                              <th className="p-2">Action</th>
                           </tr>
                        </thead>
                        <tbody className="text-white text-left">
                           {temporaryDrivers.map((driver, index) => (
                              <tr key={index} className="border-t">
                                 <td className="p-2 text-white text-center">{driver.first_name}</td>
                                 <td className="p-2 text-white text-center">{driver.last_name}</td>
                                 <td className="p-2 text-white text-center">{driver.phone_number}</td>
                                 <td className="p-2 text-white flex items-center justify-center">
                                    <button
                                       type="button"
                                       onClick={() => removeTempDriver(index)}
                                       className="flex flex-row items-center text-red-500 hover:underline gap-1 align-center"
                                    >
                                       <BiTrash />
                                       Remove
                                    </button>
                                 </td>
                              </tr>
                           ))}
                           {temporaryDrivers.length === 0 && (
                              <tr>
                                 <td colSpan={4} className="p-3 text-center text-gray-400">No drivers added yet</td>
                              </tr>
                           )}
                        </tbody>
                     </table>
                  </div>
                  
                  {temporaryDrivers.length > 0 && (
                     <div className="flex mt-5">
                     <Button type="button" className="flex flex-row self-end bg-blue-600 text-white" onClick={submitAllDrivers}>
                        <FaSave className="mr-1" />
                       {loadingSubmission ? "Submitting..." : "Submit All Drivers"}
                     </Button>
                     </div>
                  )}
                  
               </div>
            </form>

      </div>
      </>
   )
}

export default AddDriver;